import React, { useMemo, useState } from "react";
import "./TpcCurriculumExplorer.css";

import {
  ArrowRight,
  BookOpen,
  CalendarDays,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  Layers,
  ListChecks,
  Sparkles,
} from "lucide-react";

import chartBg from "../../images/Chart 1.webp";
import { curriculumUnits, monthMeta } from "./TpcCurriculumExplorer.data";

const UNIT_PATTERN = [2, 3];
const WEEKS_PER_MONTH = 4;

const marketCandles = [
  "green",
  "green",
  "red",
  "green",
  "gold",
  "red",
  "green",
  "green",
  "red",
  "green",
  "gold",
  "green",
];

const MODULE_COPY = [
  {
    tone: "foundation",
    eyebrow: "Foundation",
    title: "Market Literacy & Chart Foundations",
    summary:
      "Learn the language of price, chart structure and platform safety before strategy work.",
    outcome:
      "Read simple trends, ranges, breakouts and key levels with clarity.",
    checkpoints: ["Chart language", "Platform safety", "Clean markups"],
  },
  {
    tone: "practice",
    eyebrow: "Practice",
    title: "Execution, Risk & Deliberate Practice",
    summary:
      "Build the habits that protect capital: risk rules, journaling and repeatable setup research.",
    outcome: "Plan, size and review trades through a written process.",
    checkpoints: ["Risk controls", "Journal routine", "Strategy hypothesis"],
  },
  {
    tone: "capstone",
    eyebrow: "Capstone",
    title: "Playbook, Simulation & Capstone",
    summary:
      "Turn one researched setup into a tested playbook through simulation, review and final presentation.",
    outcome:
      "Leave with one simulator-tested starter playbook and a 90-day practice roadmap.",
    checkpoints: ["Rule finalisation", "Simulation sprints", "Capstone defence"],
  },
];

function getUnitRangeLabel(units) {
  if (!units.length) {
    return "No units";
  }

  const first = units[0].unit;
  const last = units[units.length - 1].unit;

  return first === last ? `Unit ${first}` : `Units ${first}–${last}`;
}

function getWeekRangeLabel(weeks) {
  if (!weeks.length) {
    return "No weeks";
  }

  const first = weeks[0].weekNumber;
  const last = weeks[weeks.length - 1].weekNumber;

  return first === last ? `Week ${first}` : `Weeks ${first}–${last}`;
}

function getMonthRangeLabel(months) {
  if (!months.length) {
    return "No months";
  }

  const first = months[0].monthNumber;
  const last = months[months.length - 1].monthNumber;

  return first === last ? `Month ${first}` : `Months ${first}–${last}`;
}

function getWeekTitle(units) {
  if (!units.length) {
    return "Curriculum week";
  }

  const uniqueComponents = [...new Set(units.map((unit) => unit.component))];

  if (uniqueComponents.length === 1 && uniqueComponents[0]) {
    return uniqueComponents[0];
  }

  return `${units[0].title} → ${units[units.length - 1].title}`;
}

function formatUnitNumber(unitNumber) {
  return String(unitNumber).padStart(2, "0");
}

function toMonthTitleCase(value) {
  const smallWords = new Set([
    "and",
    "or",
    "the",
    "to",
    "of",
    "in",
    "for",
    "with",
    "a",
    "an",
  ]);

  return value
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((word, index) => {
      const normalizedWord = word.toLowerCase();

      if (index > 0 && smallWords.has(normalizedWord)) {
        return normalizedWord;
      }

      if (/^[A-Z0-9:&/+.-]{2,}$/.test(word)) {
        return word;
      }

      return normalizedWord.replace(
        /(^|[-/])([a-z])/g,
        (_, separator, letter) => {
          return `${separator}${letter.toUpperCase()}`;
        }
      );
    })
    .join(" ");
}

function getCleanMonthTitle(monthInfo, monthNumber) {
  const fallbackTitle = `Month ${monthNumber}`;
  const rawTitle = monthInfo?.title || fallbackTitle;
  const cleanTitle = rawTitle
    .replace(
      /^module\s+\d+\s*(starts|opens|begins|closes|ends)?\s*:?\s*/i,
      ""
    )
    .replace(/^month\s+\d+\s*:?\s*/i, "")
    .trim();

  return cleanTitle ? toMonthTitleCase(cleanTitle) : fallbackTitle;
}

function buildPartTimePlan(units) {
  let cursor = 0;
  let partTimeWeek = 1;
  const weeks = [];

  while (cursor < units.length) {
    const unitCount = UNIT_PATTERN[(partTimeWeek - 1) % UNIT_PATTERN.length];
    const weekUnits = units.slice(cursor, cursor + unitCount);
    const monthNumber = Math.ceil(partTimeWeek / WEEKS_PER_MONTH);
    const weekInMonth = ((partTimeWeek - 1) % WEEKS_PER_MONTH) + 1;
    const monthInfo = monthMeta[monthNumber - 1] || {};

    weeks.push({
      id: `week-${partTimeWeek}`,
      weekNumber: partTimeWeek,
      weekInMonth,
      monthNumber,
      module: monthInfo.module || `Module ${Math.ceil(monthNumber / 2)}`,
      title: getWeekTitle(weekUnits),
      unitRange: getUnitRangeLabel(weekUnits),
      units: weekUnits,
      unitCount: weekUnits.length,
    });

    cursor += unitCount;
    partTimeWeek += 1;
  }

  const totalMonths = Math.max(
    monthMeta.length,
    Math.ceil(weeks.length / WEEKS_PER_MONTH)
  );

  return Array.from({ length: totalMonths }, (_, index) => {
    const monthNumber = index + 1;
    const monthInfo = monthMeta[index] || {};
    const monthWeeks = weeks.filter((week) => week.monthNumber === monthNumber);
    const monthUnits = monthWeeks.flatMap((week) => week.units);

    return {
      ...monthInfo,
      id: `month-${monthNumber}`,
      monthNumber,
      title: getCleanMonthTitle(monthInfo, monthNumber),
      subtitle:
        monthInfo.subtitle ||
        "Build the next layer of your technical trading process.",
      module: monthInfo.module || `Module ${Math.ceil(monthNumber / 2)}`,
      weeks: monthWeeks,
      units: monthUnits,
      unitRange: getUnitRangeLabel(monthUnits),
      unitCount: monthUnits.length,
      weekRange: getWeekRangeLabel(monthWeeks),
    };
  }).filter((month) => month.weeks.length > 0 || month.units.length > 0);
}

function buildModulePlan(months) {
  const moduleCount = Math.ceil(months.length / 2);

  return Array.from({ length: moduleCount }, (_, index) => {
    const moduleNumber = index + 1;
    const copy = MODULE_COPY[index] || {
      tone: "foundation",
      eyebrow: `Module ${moduleNumber}`,
      title: `Module ${moduleNumber}`,
      summary: "Build the next layer of your trading process.",
      outcome: "Move forward with clear evidence and practical feedback.",
      checkpoints: ["Learn", "Practise", "Review"],
    };

    const moduleMonths = months.slice(index * 2, index * 2 + 2);
    const moduleWeeks = moduleMonths.flatMap((month) => month.weeks || []);
    const moduleUnits = moduleMonths.flatMap((month) => month.units || []);

    return {
      ...copy,
      id: `module-${moduleNumber}`,
      moduleNumber,
      months: moduleMonths,
      weeks: moduleWeeks,
      units: moduleUnits,
      monthRange: getMonthRangeLabel(moduleMonths),
      weekRange: getWeekRangeLabel(moduleWeeks),
      unitRange: getUnitRangeLabel(moduleUnits),
      unitCount: moduleUnits.length,
    };
  }).filter((moduleItem) => moduleItem.months.length > 0);
}

function SummaryCard({ icon, value, label, trend = "green" }) {
  return (
    <article
      className="trpc-curriculumExplorer__summaryCard"
      data-trend={trend}
    >
      <span className="trpc-curriculumExplorer__summaryIcon" aria-hidden="true">
        {icon}
      </span>

      <div>
        <strong>{value}</strong>
        <p>{label}</p>
      </div>
    </article>
  );
}

function CurriculumWeek({ week, isOpen, onToggle }) {
  return (
    <article
      className={`trpc-curriculumExplorer__week ${
        isOpen ? "is-open" : ""
      }`}
    >
      <button
        type="button"
        className="trpc-curriculumExplorer__weekToggle"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span className="trpc-curriculumExplorer__weekPill">
          Week {week.weekNumber}
        </span>

        <span className="trpc-curriculumExplorer__weekCopy">
          <strong>{week.title}</strong>
          <span>
            {week.unitRange} · {week.unitCount}{" "}
            {week.unitCount === 1 ? "unit" : "units"}
          </span>
        </span>

        <span className="trpc-curriculumExplorer__weekIcon" aria-hidden="true">
          {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </span>
      </button>

      {isOpen ? (
        <div className="trpc-curriculumExplorer__weekBody">
          <ol className="trpc-curriculumExplorer__weekUnitList">
            {week.units.map((unit) => (
              <li
                className="trpc-curriculumExplorer__weekUnitItem"
                key={unit.unit}
              >
                <span className="trpc-curriculumExplorer__unitBadge">
                  {formatUnitNumber(unit.unit)}
                </span>

                <div>
                  <span>{unit.component || unit.phase || "Trading unit"}</span>
                  <strong>{unit.title}</strong>
                  <p>{unit.focus || unit.description}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </article>
  );
}

function CurriculumMonth({
  month,
  isOpen,
  openWeekId,
  onMonthToggle,
  onWeekToggle,
}) {
  return (
    <article
      className={`trpc-curriculumExplorer__monthAccordion ${
        isOpen ? "is-open" : ""
      }`}
    >
      <button
        type="button"
        className="trpc-curriculumExplorer__monthToggle"
        onClick={onMonthToggle}
        aria-expanded={isOpen}
      >
        <span className="trpc-curriculumExplorer__monthNode">
          <span>Month</span>
          <strong>{month.monthNumber}</strong>
        </span>

        <span className="trpc-curriculumExplorer__monthCopy">
          <strong>{month.title}</strong>
          <span>{month.subtitle}</span>
        </span>

        <span className="trpc-curriculumExplorer__monthIcon" aria-hidden="true">
          {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </span>
      </button>

      {isOpen ? (
        <div className="trpc-curriculumExplorer__monthBody">
          <div
            className="trpc-curriculumExplorer__monthWeeks"
            aria-label={`Weeks for month ${month.monthNumber}`}
          >
            {month.weeks.map((week) => (
              <CurriculumWeek
                key={week.id}
                week={week}
                isOpen={openWeekId === week.id}
                onToggle={() => onWeekToggle(week.id)}
              />
            ))}
          </div>
        </div>
      ) : null}
    </article>
  );
}

function CurriculumModule({
  moduleItem,
  isOpen,
  openMonthId,
  openWeekId,
  onModuleToggle,
  onMonthToggle,
  onWeekToggle,
}) {
  const toneClass = `trpc-curriculumExplorer__module--${moduleItem.tone}`;

  return (
    <article
      className={`trpc-curriculumExplorer__module ${toneClass} ${
        isOpen ? "is-open" : ""
      }`}
      data-tone={moduleItem.tone}
    >
      <button
        type="button"
        className="trpc-curriculumExplorer__moduleToggle"
        onClick={onModuleToggle}
        aria-expanded={isOpen}
      >
        <span className="trpc-curriculumExplorer__moduleNode">
          <span>Module</span>
          <strong>{String(moduleItem.moduleNumber).padStart(2, "0")}</strong>
        </span>

        <span className="trpc-curriculumExplorer__moduleCopy">
          <span className="trpc-curriculumExplorer__moduleMeta">
            {moduleItem.eyebrow} · {moduleItem.monthRange} ·{" "}
            {moduleItem.weekRange}
          </span>

          <strong>{moduleItem.title}</strong>
          <span>{moduleItem.summary}</span>
        </span>

        <span className="trpc-curriculumExplorer__moduleStats">
          <span>{moduleItem.unitCount} units</span>
          {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </span>
      </button>

      {isOpen ? (
        <div className="trpc-curriculumExplorer__moduleBody">
          <div className="trpc-curriculumExplorer__outcomePanel">
            <span>Outcome</span>

            <strong>{moduleItem.outcome}</strong>

            <ul aria-label={`${moduleItem.title} checkpoints`}>
              {moduleItem.checkpoints.map((checkpoint) => (
                <li key={checkpoint}>{checkpoint}</li>
              ))}
            </ul>
          </div>

          <div
            className="trpc-curriculumExplorer__monthAccordionList"
            aria-label={`${moduleItem.title} monthly pathway`}
          >
            {moduleItem.months.map((month) => (
              <CurriculumMonth
                key={month.id}
                month={month}
                isOpen={openMonthId === month.id}
                openWeekId={openWeekId}
                onMonthToggle={() => onMonthToggle(month)}
                onWeekToggle={onWeekToggle}
              />
            ))}
          </div>
        </div>
      ) : null}
    </article>
  );
}

function TpcCurriculumExplorer({
  applyHref = "https://andus.co.za/trade-register",
}) {
  const months = useMemo(() => buildPartTimePlan(curriculumUnits), []);
  const modules = useMemo(() => buildModulePlan(months), [months]);
  const allWeeks = useMemo(
    () => months.flatMap((month) => month.weeks),
    [months]
  );

  const [openModuleId, setOpenModuleId] = useState("module-1");
  const [openMonthId, setOpenMonthId] = useState("month-1");
  const [openWeekId, setOpenWeekId] = useState("week-1");

  const handleModuleToggle = (moduleItem) => {
    const willOpen = openModuleId !== moduleItem.id;
    const firstMonth = moduleItem.months[0];

    setOpenModuleId(willOpen ? moduleItem.id : "");
    setOpenMonthId(willOpen ? firstMonth?.id || "" : "");
    setOpenWeekId(willOpen ? firstMonth?.weeks[0]?.id || "" : "");
  };

  const handleMonthToggle = (month) => {
    const willOpen = openMonthId !== month.id;

    setOpenMonthId(willOpen ? month.id : "");
    setOpenWeekId(willOpen ? month.weeks[0]?.id || "" : "");
  };

  const handleWeekToggle = (weekId) => {
    setOpenWeekId((current) => (current === weekId ? "" : weekId));
  };

  return (
    <section
      id="tpc-curriculum-explorer"
      className="trpc-curriculumExplorer"
      aria-labelledby="trpc-curriculumExplorer-title"
    >
      <div className="trpc-curriculumExplorer__marketLayer" aria-hidden="true">
        <span className="trpc-curriculumExplorer__priceLine trpc-curriculumExplorer__priceLine--one" />
        <span className="trpc-curriculumExplorer__priceLine trpc-curriculumExplorer__priceLine--two" />

        <div className="trpc-curriculumExplorer__candleGroup">
          {marketCandles.map((trend, index) => (
            <span
              key={`${trend}-${index}`}
              className="trpc-curriculumExplorer__candle"
              data-trend={trend}
            />
          ))}
        </div>
      </div>

      <div className="trpc-curriculumExplorer__shell">
        <div className="trpc-curriculumExplorer__hero">
          <img
            className="trpc-curriculumExplorer__heroChart"
            src={chartBg}
            alt=""
            decoding="async"
            loading="lazy"
          />

          <div
            className="trpc-curriculumExplorer__heroShade"
            aria-hidden="true"
          />

          <div
            className="trpc-curriculumExplorer__heroTerminalTop"
            aria-hidden="true"
          >
            <span />
            <span />
            <span />
          </div>

          <div
            className="trpc-curriculumExplorer__heroCandleStrip"
            aria-hidden="true"
          >
            {marketCandles.slice(0, 8).map((trend, index) => (
              <span key={`${trend}-hero-${index}`} data-trend={trend} />
            ))}
          </div>

          <div className="trpc-curriculumExplorer__headCopy">
            <div className="trpc-curriculumExplorer__kicker">
              <Sparkles size={16} aria-hidden="true" />
              Program Overview
            </div>

            <h2
              id="trpc-curriculumExplorer-title"
              className="trpc-curriculumExplorer__title"
            >
              Your trading pathway
            </h2>

            <p className="trpc-curriculumExplorer__intro">
              From market basics to one simulator-tested price-action playbook.
            </p>
          </div>

          <div
            className="trpc-curriculumExplorer__summaryGrid"
            aria-label="Program summary"
          >
            <SummaryCard
              icon={<Layers size={18} />}
              value={`${modules.length} modules`}
              label="Clear training stages."
              trend="green"
            />

            <SummaryCard
              icon={<CalendarDays size={18} />}
              value={`${allWeeks.length} part-time weeks`}
              label="Steady part-time pace."
              trend="gold"
            />

            <SummaryCard
              icon={<ListChecks size={18} />}
              value={`${curriculumUnits.length} units`}
              label="Focused touchpoints."
              trend="red"
            />

            <SummaryCard
              icon={<ClipboardCheck size={18} />}
              value="1 final playbook"
              label="Replay-tested result."
              trend="green"
            />
          </div>
        </div>

        <div className="trpc-curriculumExplorer__rhythmStrip" role="note">
          <span
            className="trpc-curriculumExplorer__rhythmIcon"
            aria-hidden="true"
          >
            <BookOpen size={18} />
          </span>

          <div className="trpc-curriculumExplorer__rhythmCopy">
            <span>Explore your program</span>
            <p>Tap a module, then a month, to reveal each week and its units.</p>
          </div>
        </div>

        <div className="trpc-curriculumExplorer__moduleList">
          {modules.map((moduleItem) => (
            <CurriculumModule
              key={moduleItem.id}
              moduleItem={moduleItem}
              isOpen={openModuleId === moduleItem.id}
              openMonthId={openMonthId}
              openWeekId={openWeekId}
              onModuleToggle={() => handleModuleToggle(moduleItem)}
              onMonthToggle={handleMonthToggle}
              onWeekToggle={handleWeekToggle}
            />
          ))}
        </div>

        <div className="trpc-curriculumExplorer__footerCta">
          <div>
            <span>Ready to start?</span>
            <strong>
              Apply for the next Price Action Trader Bootcamp cohort.
            </strong>
          </div>

          <a
            className="trpc-curriculumExplorer__footerBtn"
            href={applyHref}
            target="_blank"
            rel="noopener noreferrer"
            data-candle="green"
          >
            <span>Apply Now</span>
            <ArrowRight size={18} aria-hidden="true" />
          </a>
        </div>
      </div>
    </section>
  );
}

export default TpcCurriculumExplorer;